import { components, paths } from "@/api-schema";
import {
  PathsV1UserSearchPostParametersQueryExtend,
  PathsV1UserSearchPostParametersQueryOrderByDirection,
  PathsV1UserSearchPostParametersQueryOrderByField,
  DataExportType,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useUserSearchQuery } from "@/common/api/user/use-user-search.query";
import { getUserDetailPath } from "@/common/helpers/get-user-detail-path";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures, TimestampSortField } from "@/components/data-table/types";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { DefaultTableSubComponent } from "./_components/default-table-sub-component";
import { userTableDefaultColumns } from "./user-table-columns";

type User = components["schemas"]["User"] & RowWithIdentifier;
type UserSorting = NonNullable<paths["/v1/user/search"]["post"]["parameters"]["query"]["orderBy"]>;
type UserQueryParams = paths["/v1/user/search"]["post"]["parameters"]["query"];
type UserRequestBody = paths["/v1/user/search"]["post"]["requestBody"]["content"]["application/json"];
type UserExtend = NonNullable<paths["/v1/user/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: UserExtend = [
  PathsV1UserSearchPostParametersQueryExtend.Address,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch_Company,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch_Brands,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch_Address,
  PathsV1UserSearchPostParametersQueryExtend.Departments,
  PathsV1UserSearchPostParametersQueryExtend.Occupations,
] as const;

const DEFAULT_COLUMNS = [
  userTableDefaultColumns.columnAvatar,
  userTableDefaultColumns.columnName,
  userTableDefaultColumns.columnBranch,
  userTableDefaultColumns.columnContact,
  userTableDefaultColumns.columnDepartment,
];

const USER_TABLE_CONFIG: PredefinedTableConfig<UserSorting, "name"> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1UserSearchPostParametersQueryOrderByField.LastName,
    },
    {
      direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1UserSearchPostParametersQueryOrderByField.FirstName,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: "common:SearchUser",
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: true,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: false,
    showDelete: false,
    viewLabel: "phonebook:ViewProfile",
  } as const,
  sortingMapping: {
    name: ["lastName", "firstName"],
  } as const,
  sortColumns: [{ id: "name", label: () => i18next.t("common:Name") }],
  timestampSortFields: [TimestampSortField.CreatedAt, TimestampSortField.UpdatedAt],
  exportType: DataExportType.User,
} as const;

const USER_TABLE_QUERY_HOOKS: DataFetchHooks<User, UserQueryParams, UserRequestBody> = {
  useSearchQuery: useUserSearchQuery,
} as const;

export interface UserTableProps extends BasePredefinedTableProps<User, UserSorting, UserQueryParams, UserRequestBody> {
  extend?: UserExtend;
}

const BaseUserTable = createBasePredefinedTable({
  config: USER_TABLE_CONFIG,
  queryHooks: USER_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function UserTable(props: UserTableProps) {
  const [_, navigate] = useLocation();
  const { t } = useTranslation("common");

  function navigateToUserProfile(row: User) {
    navigate(getUserDetailPath(row.uuid));
  }

  const {
    name = "user-table",
    extend = DEFAULT_EXTEND,
    onRowNavigate = (row: User) => getUserDetailPath(row.uuid),
    onView = navigateToUserProfile,
    ...restProps
  } = props;

  return (
    <BaseUserTable
      searchPlaceholder={t("common:SearchUser")}
      name={name}
      extend={extend}
      onRowNavigate={onRowNavigate}
      onView={onView}
      subComponent={DefaultTableSubComponent}
      {...restProps}
    />
  );
}
